/**
 * Tiny Web Audio confirmation tones for scan-driven flows.
 *
 * - `playSuccessTone(level)` — short rising "ding" (880 Hz → 1320 Hz, 90 ms)
 * - `playErrorTone(level)`   — short low "buzz" (180 Hz square wave, 220 ms)
 *
 * `level` is one of 'off' | 'quiet' | 'normal' | 'loud'. When omitted the
 * helper reads `localStorage.palletScanSound` so callers that don't track
 * state still respect the operator's saved preference. 'off' is a no-op.
 *
 * AudioContext is lazy-instantiated on the first call because browsers
 * require a prior user-gesture before audio can start. Scan flows always
 * begin with a click/tap (or trigger pull, which the browser treats as
 * keyboard input — also a gesture), so the first tone reliably plays.
 *
 * No-op safe — silently does nothing when the host has no AudioContext
 * (older Safari, sandboxed iframes), so callers don't need to feature-test.
 */

let _ctx = null;

function getCtx() {
  if (_ctx) return _ctx;
  const Ctor = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  if (!Ctor) return null;
  try { _ctx = new Ctor(); } catch { _ctx = null; }
  return _ctx;
}

// Multiplier applied to the base gain of each tone. Loud is intentionally
// capped at 2.0× so the linearRamp never clips into distortion territory
// (base gains stay ≤ 0.20 so 0.20 × 2 = 0.40, well under 1.0).
const LEVEL_GAIN = { off: 0, quiet: 0.4, normal: 1.0, loud: 2.0 };

// Read the operator's saved preference. Legacy 'on' maps to 'normal' so
// existing installs after the first toggle release upgrade cleanly.
export function readSoundLevel() {
  try {
    const raw = typeof window !== 'undefined'
      ? window.localStorage.getItem('palletScanSound') : null;
    if (raw === 'off' || raw === 'quiet' || raw === 'normal' || raw === 'loud') return raw;
    if (raw === 'on') return 'normal';      // legacy single-toggle value
    return 'normal';                         // default for first-time users
  } catch {
    return 'normal';
  }
}

function tone({ freq = 880, type = 'sine', duration = 0.09, freqEnd = null, gain = 0.18 }, level) {
  const lvl = level || readSoundLevel();
  const mult = LEVEL_GAIN[lvl] ?? LEVEL_GAIN.normal;
  if (!mult) return;                         // 'off' or unknown → silent
  const ctx = getCtx();
  if (!ctx) return;
  // Some browsers suspend the context until a fresh gesture — try resuming.
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, ctx.currentTime);
  if (freqEnd) osc.frequency.linearRampToValueAtTime(freqEnd, ctx.currentTime + duration);
  // Tiny attack/decay envelope so we don't get a popping click on start/stop.
  const peak = Math.min(0.95, gain * mult);
  g.gain.setValueAtTime(0, ctx.currentTime);
  g.gain.linearRampToValueAtTime(peak, ctx.currentTime + 0.005);
  g.gain.linearRampToValueAtTime(0, ctx.currentTime + duration);
  osc.connect(g).connect(ctx.destination);
  osc.start();
  osc.stop(ctx.currentTime + duration + 0.02);
}

export function playSuccessTone(level) {
  tone({ freq: 880, freqEnd: 1320, type: 'sine', duration: 0.09, gain: 0.2 }, level);
}

export function playErrorTone(level) {
  // Two short low square-wave pulses = a clear "wrong" buzz.
  tone({ freq: 180, type: 'square', duration: 0.11, gain: 0.18 }, level);
  setTimeout(() => tone({ freq: 140, type: 'square', duration: 0.11, gain: 0.18 }, level), 110);
}
